import $ from 'jquery';
import { getState } from '~src/state';
import { Guideline } from './Guideline';
import {
  GUIDELINE_COLOR,
  GUIDELINE_WIDTH,
  GUIDELINE_SNAP_COLOR,
  SNAP_HIGHLIGHT_WIDTH,
} from './constants';
import { findNearestRulerMark, findGuidelineSnapPoint } from './snapFunctions';

// Guidelines array
export let guidelines = [];

// Dragging state
export let isDraggingGuideline = false;

export const createGuideline = (orientation, position, rulerUnit) => {
  const canvas = window.canvas;
  let coords;

  // Position is already in canvas coordinates (zoom-adjusted)
  const initialPosition = position;

  if (orientation === 'horizontal') {
    coords = [0, initialPosition, canvas.getWidth(), initialPosition];
  } else {
    coords = [initialPosition, 0, initialPosition, canvas.getHeight()];
  }

  const guideline = new Guideline(coords, { orientation: orientation });

  // Add guideline to canvas
  canvas.add(guideline);
  guidelines.push(guideline);
  canvas.setActiveObject(guideline);
  canvas.renderAll();

  // Start dragging immediately
  isDraggingGuideline = true;
  let isMouseDown = true; // Track if mouse is still pressed
  let isDragMode = false; // Track if we're in drag mode (mouse moved while pressed)
  let clickToPlaceReady = false; // Track if we're ready to place on next click

  const handleMouseMove = (e) => {
    if (!isDraggingGuideline) return;

    // If mouse is down and moving, we're in drag mode
    if (isMouseDown) {
      isDragMode = true;
    }

    // Continue following mouse unless we're done
    if (!isDraggingGuideline) return;

    // Use the pointer method which already accounts for viewport transform and zoom
    const pointer = canvas.getPointer(e);
    let targetPosition;

    if (orientation === 'horizontal') {
      targetPosition = pointer.y;
    } else {
      targetPosition = pointer.x;
    }

    // Clamp position to canvas bounds
    if (orientation === 'horizontal') {
      targetPosition = Math.max(
        0,
        Math.min(targetPosition, canvas.getHeight())
      );
    } else {
      targetPosition = Math.max(0, Math.min(targetPosition, canvas.getWidth()));
    }

    // First check for ruler mark snapping
    const rulerSnapThreshold = 5; // Tighter threshold for ruler marks
    const nearestRulerMark = findNearestRulerMark(
      targetPosition,
      rulerSnapThreshold,
      rulerUnit
    );

    // Then check for object/guideline snap points
    const snapResult = findGuidelineSnapPoint(
      targetPosition,
      orientation,
      guideline
    );

    // Decide which snap to use (ruler marks have priority when closer)
    let finalSnapPosition = null;
    let snapType = null;

    if (nearestRulerMark !== null && snapResult.position !== null) {
      // Both snaps available, choose the closer one
      const rulerDistance = Math.abs(targetPosition - nearestRulerMark);
      const objectDistance = Math.abs(targetPosition - snapResult.position);

      if (rulerDistance <= objectDistance) {
        finalSnapPosition = nearestRulerMark;
        snapType = 'ruler';
      } else {
        finalSnapPosition = snapResult.position;
        snapType = 'object';
      }
    } else if (nearestRulerMark !== null) {
      finalSnapPosition = nearestRulerMark;
      snapType = 'ruler';
    } else if (snapResult.position !== null) {
      finalSnapPosition = snapResult.position;
      snapType = 'object';
    }

    if (finalSnapPosition !== null) {
      // Snap to the position
      targetPosition = finalSnapPosition;

      // Change guideline appearance when snapped
      // Use different colors for different snap types
      guideline.set({
        stroke: snapType === 'ruler' ? '#00ff00' : GUIDELINE_SNAP_COLOR,
        strokeWidth: SNAP_HIGHLIGHT_WIDTH,
        padding: guideline.customPadding || guideline.padding, // Preserve padding
      });
    } else {
      // No snap point found, use regular appearance
      guideline.set({
        stroke: GUIDELINE_COLOR,
        strokeWidth: GUIDELINE_WIDTH,
        padding: guideline.customPadding || guideline.padding, // Preserve padding
      });
    }

    // Always update position based on current targetPosition
    if (orientation === 'horizontal') {
      guideline.set({
        x1: 0,
        x2: canvas.getWidth(),
        y1: targetPosition,
        y2: targetPosition,
        top: targetPosition, // Also update top position
        left: 0, // Ensure left is at canvas start
        width: canvas.getWidth(), // Update width to match canvas
        padding: guideline.customPadding || guideline.padding, // Preserve padding
      });
    } else {
      guideline.set({
        x1: targetPosition,
        x2: targetPosition,
        y1: 0,
        y2: canvas.getHeight(),
        left: targetPosition, // Also update left position
        top: 0, // Ensure top is at canvas start
        height: canvas.getHeight(), // Update height to match canvas
        padding: guideline.customPadding || guideline.padding, // Preserve padding
      });
    }

    guideline.setCoords();
    canvas.renderAll();
  };

  // Declare functions first to avoid hoisting issues
  let finishPlacement;

  const handleMouseUp = () => {
    isMouseDown = false;

    // If we're in drag mode, finish on mouse up
    if (isDragMode) {
      finishPlacement();
    } else {
      // We're in click-to-place mode, enable placement on next click
      setTimeout(() => {
        clickToPlaceReady = true;
      }, 100); // Small delay to avoid immediate click event
    }
  };

  const handleMouseDown = (e) => {
    // If we're ready to place (second click in click-to-place mode)
    if (clickToPlaceReady && !isDragMode) {
      finishPlacement();
      e.preventDefault();
      e.stopPropagation();
    }
  };

  // Define finishPlacement after handlers are defined
  finishPlacement = () => {
    isDraggingGuideline = false;
    clickToPlaceReady = false;
    $(document).off('mousemove.guideline', handleMouseMove);
    $(document).off('mouseup.guideline', handleMouseUp);
    $(document).off('mousedown.guideline', handleMouseDown);

    // Reset to normal appearance after placing
    guideline.set({
      opacity: 1,
      padding: guideline.customPadding || guideline.padding, // Preserve padding
    });
    canvas.renderAll();

    // Add to guidelines array
    guidelines.push(guideline);
  };

  // Listen on document level so drag works from ruler to canvas
  // Use namespaced events to avoid interfering with other handlers
  $(document).on('mousemove.guideline', handleMouseMove);
  $(document).on('mouseup.guideline', handleMouseUp);
  $(document).on('mousedown.guideline', handleMouseDown);
};

export const removeGuideline = (guideline) => {
  const canvas = window.canvas;
  const index = guidelines.indexOf(guideline);

  if (index > -1) {
    guidelines.splice(index, 1);
    canvas.remove(guideline);
    canvas.renderAll();
  }
};

export const clearGuidelines = () => {
  guidelines.forEach((guideline) => {
    window.canvas.remove(guideline);
  });
  guidelines = [];
  window.canvas.renderAll();
};

export const updateGuidelinesVisibility = (visible) => {
  guidelines.forEach((guideline) => {
    guideline.set('visible', visible);
  });
  window.canvas.renderAll();
};

export const loadExistingGuidelines = () => {
  // Re-populate guidelines array with any existing guidelines on canvas
  // This handles the case where guidelines were loaded from JSON
  guidelines = [];
  const canvas = window.canvas;
  const canvasObjects = canvas.getObjects();
  canvasObjects.forEach((obj) => {
    if (obj.isGuideline && obj.type === 'Guideline') {
      // Fix guideline coordinates to ensure they span the full canvas
      if (obj.orientation === 'horizontal') {
        obj.set({
          x1: 0,
          x2: canvas.getWidth(),
          left: 0,
          width: canvas.getWidth(),
        });
      } else if (obj.orientation === 'vertical') {
        obj.set({
          y1: 0,
          y2: canvas.getHeight(),
          top: 0,
          height: canvas.getHeight(),
        });
      }
      obj.setCoords();

      guidelines.push(obj);
      // Ensure the guideline is visible if ruler is enabled
      const isRulerEnabled = getState('rulerEnabled', false);
      obj.set('visible', isRulerEnabled);
    }
  });

  if (guidelines.length > 0) {
    console.log(`Found ${guidelines.length} existing guidelines on canvas`);
    canvas.renderAll();
  }
};
